import { useEffect, useRef } from 'react';

/**
 * TRACI-628: a staff member pressed "Send to a rep" for this chat on the live chat monitor
 * (/qa/live). The widget has no open channel while idle, so it asks, on a slow poll, whether
 * a send is waiting (GET /agent/staff-handoff/{sessionId}; taking it removes it, so one tab
 * acts on it once). On yes it runs the shopper's own escalate (`onSend`), so the provider,
 * the department, the caps and the connecting UI are exactly a button press's.
 *
 * Polls only while it could matter: tab visible, a session, the shopper has said something
 * (restored messages count, so a reload keeps polling), no handoff in progress, and the last
 * activity under 35 minutes old (the monitor's live record; an older chat cannot be sent).
 * A send that lands while the AI is still answering waits for the answer to finish.
 */
export const STAFF_HANDOFF_POLL_MS = 12_000;
export const STAFF_HANDOFF_JITTER_MS = 3_000;
export const STAFF_HANDOFF_MAX_IDLE_MS = 35 * 60_000;

export interface StaffHandoffPollOptions {
  sessionId: string | null;
  hasShopperMessage: boolean;
  handoffIdle: boolean;          // handoff state idle or ended
  busy: boolean;                 // the AI is answering (loading or streaming)
  lastActivityAt: number | null; // ms epoch of the newest message
  check: (sessionId: string) => Promise<StaffSend | null>;
  onSend: (send: StaffSend) => void;
  now?: () => number;
}

/** A staff send the widget took: the team staff chose, or null (the chat picks, as when the
 * customer asks for a person). */
export interface StaffSend {
  department: string | null;
}

export function useStaffHandoffPoll(opts: StaffHandoffPollOptions): void {
  const { sessionId, hasShopperMessage, handoffIdle, busy, lastActivityAt } = opts;
  const latest = useRef(opts);
  latest.current = opts;
  const pendingRef = useRef<StaffSend | null>(null);

  // A send taken mid-answer fires once the answer is done, and only while the tab is
  // showing and the chat is still recent (the same guards as the poll). A handoff that starts
  // meanwhile (the shopper's own press) or a new session spends it instead.
  const firePending = () => {
    const o = latest.current;
    const held = pendingRef.current;
    if (!held || o.busy || !o.handoffIdle) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    pendingRef.current = null;
    const now = (o.now ?? Date.now)();
    if (o.lastActivityAt != null && now - o.lastActivityAt > STAFF_HANDOFF_MAX_IDLE_MS) return;
    o.onSend(held);
  };
  useEffect(() => {
    if (!handoffIdle) pendingRef.current = null;
    else firePending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, handoffIdle]);
  useEffect(() => { pendingRef.current = null; }, [sessionId]);

  const active = Boolean(sessionId) && hasShopperMessage && handoffIdle;
  useEffect(() => {
    if (!active || !sessionId) return undefined;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let inFlight = false;

    const tick = async () => {
      const o = latest.current;
      const now = (o.now ?? Date.now)();
      if (!alive || inFlight || pendingRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (o.lastActivityAt != null && now - o.lastActivityAt > STAFF_HANDOFF_MAX_IDLE_MS) return;
      inFlight = true;
      try {
        const send = await Promise.resolve().then(() => o.check(sessionId)).catch(() => null);
        if (!alive || !send) return;
        if (latest.current.busy || !latest.current.handoffIdle) {
          pendingRef.current = send;
          return;
        }
        latest.current.onSend(send);
      } finally {
        inFlight = false;
      }
    };
    const schedule = () => {
      timer = setTimeout(async () => {
        await tick();
        if (alive) schedule();
      }, STAFF_HANDOFF_POLL_MS + Math.floor(Math.random() * STAFF_HANDOFF_JITTER_MS));
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (pendingRef.current) firePending();
      else void tick();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // lastActivityAt is read through `latest`, so a new message does not restart the clock.
  }, [active, sessionId]);
  void lastActivityAt;
}
